<!-- TEMPLATE -->
<template>
    <div :class='customerDetailsClass'>
        <dean-two-column-layout :title="headerTitle">
            <dean-section-title-item
              v-if="canChangeCustomerStatus()"
              :icon='editIcon'
              slot="title-items"
              :content="true"
              :large="true"
              @click.native="statusChange"
            >
                Status wijzigen
            </dean-section-title-item>
            <dean-section-title-item
              v-if="canSaveCustomer()"
              :icon='saveIcon'
              slot="title-items"
              :content="true"
              :large="true"
              @click.native="processCustomer"
            >
                Opslaan
            </dean-section-title-item>
            <div class="column" slot="left">
                <dean-section-divider>Algemeen</dean-section-divider>
                <dean-form-row id="kvkNumber" v-model="customerData.number" label="KvK-nummer"
                               :disabled="! canSaveCustomer()"
                               @input="inputFieldChanged && customerData.number.length === 8 && kvkDetails()"></dean-form-row>
                <dean-form-row id="name" v-model="customer.businessName" label="Naam"
                               :disabled="! canSaveCustomer()"
                               :warning="$v.customer.businessName.$error" @focus="$v.customer.businessName.$touch()"
                               @input="inputFieldChanged" :required-field="true"></dean-form-row>
                <dean-select-form-row placeholder="Kies een bedrijfsvorm"
                                      label='Bedrijfsvorm klant'
                                      :options='personTypes'
                                      :value='currentType' id="bedrijfsvorm"
                                      :requiredField="false"
                                      :clearable="true"
                                      :disabled="! canSaveCustomer()"
                                      @input='personTypeFieldChanged'></dean-select-form-row>
                <dean-form-row id="email" v-model="customerData.email" label="E-mail"
                               :disabled="! canSaveCustomer()"
                               :warning="$v.customer.email.$error" @focus="$v.customer.email.$touch()"
                               @input="inputFieldChanged" :required-field="true"></dean-form-row>
                <dean-form-row id="klantreferentie" v-model="customerData.customerReference"
                               label="Klantreferentie"
                               :disabled="! canSaveCustomer()"
                               @input="inputFieldChanged"></dean-form-row>
                <dean-form-row
                  id="phoneNumber"
                  v-model="customerData.phoneNumber"
                  label="Telefoonnummer"
                  :required-field="true"
                  :disabled="! canSaveCustomer()"
                  :warning="$v.customer.phoneNumber.$error" @focus="$v.customer.phoneNumber.$touch()"
                  @input="inputFieldChanged"></dean-form-row>
                <dean-form-row id="faxNumber" v-model="customerData.faxNumber" label="Faxnummer"
                               :disabled="! canSaveCustomer()"
                               @input="inputFieldChanged"></dean-form-row>
                <dean-form-row id="bankAccount" v-model="customerData.bankAccount" label="IBAN"
                               :disabled="! canSaveCustomer()"
                               @input="inputFieldChanged"></dean-form-row>
                <dean-form-row id="vatNumber" v-model="customerData.vatNumber" label="BTW-nummer"
                               :disabled="! canSaveCustomer()"
                               @input="inputFieldChanged"></dean-form-row>
                <dean-form-row id="website" v-model="customerData.website" label="Website"
                               :disabled="! canSaveCustomer()"
                               @input="inputFieldChanged"></dean-form-row>
            </div>
            <div class="column" slot="right">
                <dean-section-divider>Adresgegevens</dean-section-divider>
                <dean-form-row id="street" v-model="customerData.street" label="Straat"
                               @input="inputFieldChanged" :required-field="true"
                               :disabled="! canSaveCustomer()"
                               :warning="$v.customer.street.$error"
                               @focus="$v.customer.street.$touch()"></dean-form-row>
                <dean-form-row id="houseNumber" v-model="customerData.houseNumber"
                               label="Huisnummer" @input="inputFieldChanged"
                               :required-field="true"
                               :disabled="! canSaveCustomer()"
                               :warning="$v.customer.houseNumber.$error"
                               @focus="$v.customer.houseNumber.$touch()"></dean-form-row>
                <dean-form-row id="houseNumberExtension" v-model="customerData.houseNumberExtension"
                               label="Toevoeging huisnummer"
                               :disabled="! canSaveCustomer()"
                               @input="inputFieldChanged"
                               @focus="$v.customer.houseNumberExtension.$touch()"></dean-form-row>
                <dean-form-row id="postalCode" v-model="customerData.postalCode" label="Postcode"
                               @input="inputFieldChanged" :required-field="true"
                               :disabled="! canSaveCustomer()"
                               :warning="$v.customer.postalCode.$error"
                               @focus="$v.customer.postalCode.$touch()"></dean-form-row>
                <dean-form-row id="city" v-model="customerData.city" label="Plaats"
                               @input="inputFieldChanged" :required-field="true"
                               :disabled="! canSaveCustomer()"
                               :warning="$v.customer.city.$error"
                               @focus="$v.customer.city.$touch()"></dean-form-row>

          <div v-if="$route.name === 'partners.customers.add'">
            <dean-section-divider>Extra handelingen</dean-section-divider>
            <!-- Create Quote right after creating customer -->
            <div id="createQuote">
              <transition name="fade" mode="out-in" v-if="agreementsLoading" id="agreementsOverlay">
                <dean-load-overlay>
                </dean-load-overlay>
              </transition>
              <dean-input-switch-form-row id="createInvoice" label="Offerte aanmaken" :value="createInvoice"
                                          @checked="inputSwitchChanged"></dean-input-switch-form-row>
              <dean-select-form-row placeholder="Kies een overeenkomst"
                                    v-if="createInvoice && agreementList.length"
                                    label='Overeenkomst voor offerte'
                                    :options='agreementList'
                                    :value="agreement"
                                    id="agreement"
                                    :requiredField="true"
                                    :clearable="true"
                                    @input='agreementFieldChanged'
                                    @focus="$v.agreement.$touch()">
              </dean-select-form-row>
            </div>
          </div>
        </div>
      </dean-two-column-layout>
    </div>
</template>

<!-- SCRIPT -->

<script lang='ts'>
  // VUE
  import {Component, Watch} from 'vue-property-decorator';
  import vSelect from 'vue-select';
  import axios from 'axios';

  import {
    businessIcon,
    commissionIcon,
    customerIcon,
    customerServiceIcon,
    deanAlertDialog,
    deanIcon,
    deanInputField,
    deanSearchField,
    deanSectionDivider,
    deanSectionTitle,
    deanSectionTitleItem,
    invoicesIcon,
    editIcon,
    messageIcon,
    offersIcon,
    ordersIcon,
    returnIcon,
    saveIcon,
    summeryIcon,
    trashIcon,
    usersIcon,
    warningIcon,
    plusIcon,
    deanLoadOverlay,
  } from '@deanone/dean-components';
  import DeanFormRow from '@/components/deanComponents/deanFormRow.vue';
  import DeanTwoColumnLayout from '@/components/deanComponents/deanTwoColumnLayout.vue';
  import deanInputSelect from '@/components/deanComponents/deanInputSelect.vue';
  import DeanSelectFormRow from '@/components/deanComponents/deanSelectFormRow.vue';
  import deanSectionTitleRemoveRestore from '@/components/deanComponents/deanSectionTitleRemoveRestore.vue';
  import DeanInputSwitchFormRow from '@/components/deanComponents/deanInputSwitchFormRow.vue';
  import {deanDialogBus} from '@/main';
  import {validationMixin} from 'vuelidate';
  import {email, required, requiredIf} from 'vuelidate/lib/validators';
  import {Validations} from 'vuelidate-property-decorators';
  import {createNotificationFromData} from '@/mixins/validations';
  import sideMenuCustomerMixin from '@/mixins/sideMenuCustomerMixin';
  import sideMenuPartnerMixin from '@/mixins/sideMenuPartnerMixin';
  import {mixins} from 'vue-class-component';

  @Component({
    components: {
      DeanSelectFormRow,
      deanAlertDialog,
      deanInputField,
      DeanTwoColumnLayout,
      deanInputSelect,
      DeanFormRow,
      deanSectionDivider,
      deanSectionTitle,
      deanSectionTitleItem,
      deanSearchField,
      vSelect,
      deanSectionTitleRemoveRestore,
      invoicesIcon,
      deanIcon,
      returnIcon,
      saveIcon,
      summeryIcon,
      customerIcon,
      businessIcon,
      usersIcon,
      offersIcon,
      ordersIcon,
      commissionIcon,
      customerServiceIcon,
      messageIcon,
      warningIcon,
      DeanInputSwitchFormRow,
      plusIcon,
      deanLoadOverlay,
    },
    mixins: [validationMixin],
  })

  export default class Details extends mixins(sideMenuCustomerMixin, sideMenuPartnerMixin) {
    public customer: any = {
      id: null,
      partner: null,
      number: null,
      businessName: '',
      type: null,
      email: null,
      customerReference: null,
      phoneNumber: null,
      faxNumber: null,
      bankAccount: null,
      vatNumber: null,
      website: '',
      street: '',
      houseNumber: '',
      houseNumberExtension: '',
      postalCode: '',
      city: '',
      partners: [],
      status: undefined,
    };
    public personTypes: Array<{ value: any, label: any }> = [];
    public returnIcon: object = returnIcon;
    public saveIcon: object = saveIcon;
    public editIcon: object = editIcon;
    public trashIcon: object = trashIcon;
    public plusIcon: object = plusIcon;
    public showSomething: boolean = false;
    public timeout?: number;

    public createInvoice: boolean = false;
    public agreementsLoading: boolean = false;
    public agreementList: Array<{}> = [];
    public agreement: any = null;

    // COMPUTED
    @Validations()
    get validations() {
      const valData: any = {
        customer: {
          businessName: {required},
          email: {required, email},
          street: {required},
          phoneNumber: {required},
          houseNumber: {required},
          houseNumberExtension: {},
          postalCode: {required},
          city: {required},
        },
        createInvoice: {required},
        agreement: {
          required: requiredIf(() => {
            return this.createInvoice;
          }),
        },
      };

      if (this.$store.getters.getUser.privileges.includes('view-partner')) {
        valData.customer.partner = {required};
      }

      return valData;
    }

    get customerDetailsClass() {
      let value = 'customer-add';
      if (this.showSomething) {
        value += ' no-scroll-container';
      }
      return value;
    }

    get customerData() {
      return this.customer;
    }

    get user() {
      return this.$store.getters.getUser;
    }

    get currentType() {
      return this.personTypes.find((personType: any) => {
        return personType.value === this.customer.type;
      });
    }

    get customerPartner() {
      return this.$route.params.pid;
    }

    get headerTitle(): string {
      return (this.$route.params.id !== undefined) ? 'Klant wijzigen' : 'Klant toevoegen aan partner';
    }

    public inputFieldChanged(field: any) {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.$store.commit('SET_CUSTOMER', this.customerData);

      }, 200);
    }

    public agreementFieldChanged(field: any) {
      this.agreement = field;
    }

    @Watch('createInvoice')
    public onCreateInvoice() {
      if (this.createInvoice) {
        this.agreementsLoading = true;
        axios.post('/-/trade/api/customer.partner.agreements.list', {
          id: this.customerPartner,
          meta: {
            sort: 'pa.created',
          },
          activeOnly: true,
        })
            .then(
                (response) => {
                  if (response.data.data.length >= 1) {
                    this.agreementList = response.data.data.map((agreement: any) => {
                      const description = agreement.description.length ? agreement.description : 'Geen beschrijving';
                      return {
                        label: `${description} (${agreement.code} - ${agreement.typeName})`,
                        value: agreement.id,
                      };
                    });
                    // Autofill with first agreement
                    this.agreement = this.agreementList[0];
                  } else {
                    this.$toast({
                      text: 'Partner heeft geen overeenkomsten. ' +
                          'Voeg een overeenkomst toe om deze functie te gebruiken.',
                      title: 'Fout',
                      theme: 'danger',
                      icon: warningIcon,
                    });
                  }
                },
                (error) => {
                  this.$toast({
                    text: 'Er is iets misgegaan bij het ophalen van overeenkomsten. Probeer het later nogmaals.',
                    title: 'Fout',
                    theme: 'danger',
                    icon: warningIcon,
                  });

                  this.createInvoice = false;
                },
            )
            .finally(
                () => {
                  this.agreementsLoading = false;
                },
            );
      } else {
        this.agreementList = [];
        this.agreement = null;
      }
    }

    public personTypeFieldChanged(field: any) {
      this.customer.type = field.value;
    }

    public customerDetails() {
      return axios.post('/-/trade/api/customer.info', {
        customerId: this.$route.params.id,
      }).then(
        (response) => {
          this.customer = {
            ...this.customer, ...{
              // partner: response.data.agreement,
              id: response.data.id,
              number: response.data.number,
              businessName: response.data.name,
              type: response.data.type,
              email: response.data.email,
              customerReference: response.data.customerReference,
              phoneNumber: response.data.phoneNumber,
              faxNumber: response.data.faxNumber,
              bankAccount: response.data.bankAccount,
              vatNumber: response.data.vatNumber,
              website: response.data.website,
              street: response.data.address?.street,
              houseNumber: response.data.address?.houseNumber,
              houseNumberExtension: response.data.address?.houseNumberExtension,
              postalCode: response.data.address?.postalCode,
              city: response.data.address?.city,
              partners: response.data.partners,
              status: response.data.status,
            },
          };

          this.buildSideMenuCustomer(this.$route.params.id);
        },
        (error) => {
          this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
        },
      );
    }

    public inputSwitchChanged(field: { id: any, value: any }) {
      const key: string = field.id;
      // @ts-ignore-next-line
      this[key] = field.value;
    }

    public initPersonTypes() {
      axios.post('/-/trade/api/person.types.list').then(
        (response) => {
          this.personTypes = [];
          for (const personType of response.data) {
            this.personTypes.push({
              value: personType.id,
              label: personType.name,
            });
          }
        },
        (error) => {
          // tslint:disable-next-line:no-console
          console.error(error);
          this.$toast({
            title: 'Fout',
            text: 'Er is iets misgegaan met het ophalen van bedrijfsvorm keuzes',
            theme: 'danger',
          });
        },
      );
    }

      public created() {
        this.customer.partner = this.$route.params.pid;
        const promises = [];
        this.$store.dispatch('setLoadOverlay', true);

        if (this.$route.params.id !== undefined) {
          promises.push(this.customerDetails());
        } else {
          this.buildSideMenuPartner();

          this.$toast({
            text: 'Voer het KVK-nummer in om de rest van de velden automatisch in te vullen.',
            title: 'Info',
            theme: 'info',
          });
        }

        promises.push(this.initPersonTypes());

        Promise.all(promises).finally(() => this.$store.dispatch('setLoadOverlay', false));
      }

    public statusChange() {
      if (this.$route.params.id && this.customerData.status) {
        deanDialogBus.$emit('open', 'CustomerStatusChangeDialog', {
          id: this.$route.params.id,
          status: this.customerData.status,
        });
      }
    }

      public processCustomer() {
        // @ts-ignore-next-line
        this.$v.customer.$touch();
        if (!this.$v.$invalid) {
          this.$store.dispatch('setLoadOverlay', true);
          axios.post('/-/trade/api/customer.save', {
            id: this.$route.params.id,
            partnerId: this.$route.params.pid,
            number: this.customerData.number,
            name: this.customerData.businessName,
            typeId: this.customerData.type,
            email: this.customerData.email,
            customerReference: this.customerData.customerReference,
            phoneNumber: this.customerData.phoneNumber,
            faxNumber: this.customerData.faxNumber,
            bankAccount: this.customerData.bankAccount,
            vatNumber: this.customerData.vatNumber,
            website: this.customerData.website,
            street: this.customerData.street,
            houseNumber: this.customerData.houseNumber,
            houseNumberExtension: this.customerData.houseNumberExtension,
            postalCode: this.customerData.postalCode,
            city: this.customerData.city,
          }).then((response) => {
            this.$toast({
              icon: saveIcon,
              text: 'Klant opgeslagen',
              theme: 'success',
              title: 'Opgeslagen',
            });

            if (this.createInvoice) {
              axios.post('/-/trade/api/quote.create', {
                customerId: response.data.id,
                paid: this.agreement.value,
              }).then(
                (quoteResponse) => {
                    this.$toast({
                      text: 'Offerte toegevoegd.',
                      theme: 'success',
                      icon: plusIcon,
                    });

                    this.$router.push({
                      name: 'quotes.edit',
                      params: {
                        id: quoteResponse.data,
                      },
                    });
                  },
                  (error) => {
                    this.$toast(this.$store.state.main.notification.messages.couldNotReceiveData);
                  },
              ).finally(
                  () => {
                    this.$store.dispatch('setLoadOverlay', false);
                  },
              );
            } else {
              this.$router.push({
                name: 'partners.customers.list',
                params: {
                  pid: this.$route.params.pid,
                },
              });
              this.$store.dispatch('setLoadOverlay', false);
            }
          },
          (error) => {
            if (
              error.response.data.errors
              && error.response.data.errors[0].code === 'ERR_CUSTOMER_KVK_NUMBER_EXISTS'
            ) {
              this.$toast({
                text: 'Klant met opgegeven KVK-nummer bestaat al',
                title: 'Fout',
                theme: 'danger',
                icon: warningIcon,
              });
            } else {
              this.$toast(this.$store.state.main.notification.messages.couldNotSendData);
            }
            this.$store.dispatch('setLoadOverlay', false);
          },
        );
      } else {
        this.$toast(createNotificationFromData(this.$v.customer));
      }
    }

    public kvkDetails() {
      const url = '/-/trade/api/kvk.info';

      axios
        .post(url, { kvkNumber: this.customerData.number })
        .then((kvkResponse: any) => {
          for (const [customerKey, customerValue] of Object.entries(this.customerData)) {
            for (const [kvkKey, kvkValue] of Object.entries(kvkResponse.data)) {
              if (
                customerKey === kvkKey
                && customerValue === '') {
                  this.customerData[customerKey] = kvkValue;
              }
            }
          }
        },
        (error: any) => {
          this.$toast({
            text: 'KVK-nummer is ongeldig.',
            title: 'Error',
            theme: 'danger',
          });
        },
      );
    }

    public canSaveCustomer(): boolean {
      if (
          this.customerData?.id
          && this.$store.getters.getUser.privileges.includes('update-customer')
      ) {
        return true;
      }

      if (
          this.customerData?.id === null
          && this.$store.getters.getUser.privileges.includes('create-customer')
      ) {
        return true;
      }

      return false;
    }

    public canChangeCustomerStatus(): boolean {
      if (
          this.customerData.id
          && this.$store.getters.getUser.privileges.includes('change-status-customer')
      ) {
        return true;
      }

      return false;
    }

    public mounted() {
      deanDialogBus.$on('customerStatusChange', () => {
        this.customerDetails();
      });
    }

    public destroyed() {
      deanDialogBus.$off('customerStatusChange');
    }
  }
</script>

<!-- STYLE -->

<style lang='scss' scoped>
    @import '~@deanone/dean-components/src/scss/vars';
</style>
